import React, { useState, useEffect, useRef } from 'react';
import styles from './Avatar.module.css';

type AvatarSize = 'sm' | 'md' | 'lg';

interface AvatarProps {
  src?: string;
  name: string;
  size?: AvatarSize;
  className?: string;
}

const sizeClassMap: Record<AvatarSize, string> = {
  sm: styles.sm,
  md: styles.md,
  lg: styles.lg,
};

export function Avatar({ src, name, size = 'md', className = '' }: AvatarProps) {
  const [loaded, setLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  // When src changes (or on first mount), check if the image is already in the
  // browser's memory cache. If so, the `load` event fires synchronously before
  // React can attach onLoad, so we must check img.complete after render.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoaded(false);
    if (imgRef.current?.complete && imgRef.current.naturalWidth > 0) {
      setLoaded(true);
    }
  }, [src]);

  return (
    <div className={[styles.avatar, sizeClassMap[size], className].filter(Boolean).join(' ')}>
      {src ? (
        <>
          {!loaded && <div className={styles.shimmer} />}
          <img
            ref={imgRef}
            src={src}
            alt={name}
            className={[styles.img, loaded ? styles['img-visible'] : styles['img-hidden']].join(' ')}
            referrerPolicy="no-referrer"
            onLoad={() => setLoaded(true)}
          />
        </>
      ) : (
        <div className={styles.fallback}>
          {name.charAt(0)}
        </div>
      )}
    </div>
  );
}
